import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './VideoLibrarySelection.module.scss';
import ChevronIcon from '../../assets/icons/chevron_down.svg?react';
import { useOnClickOutside } from '../../hooks/useClickOutside';
import { TextInput } from '../text-input/TextInput';
import Spinner from "../spinner/Spinner";
import { Video } from "@api/graphql-codegen";
import moment from "moment/moment";

type Props = {
  label?: string;
  renderLabel?: (labelComponent: React.ReactNode) => React.ReactNode;
  options: Video[];
  onChange?: (value: Video) => void;
  disabled?: boolean;
  inDropDown?: boolean;
  placeholder?: string;
  value?: Video | null;
  className?: string;
  inline?: boolean;
  subTitle?: string;
  error?: boolean;
  showSearch?: boolean;
  searchAutoFocus?: boolean;
  loading?: boolean;
  doNotSort?: boolean;
  menuClassName?: string;
};

function VideoLibrarySelection(props: Props) {
  const {
    className,
    label,
    placeholder = 'Select a Video',
    options,
    value,
    onChange,
    inDropDown,
    disabled,
    renderLabel,
    inline,
    subTitle,
    error,
    showSearch = true,
    searchAutoFocus,
    loading,
    doNotSort,
    menuClassName,
  } = props;
  const [opened, setOpened] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState('');

  useOnClickOutside(wrapperRef, () => {
    setOpened(false);
  });

  const wrapperClasses = useMemo(() => {
    const classes = [styles.wrapper, className];
    if (inline) {
      classes.push(styles.inline);
    }
    if (opened) {
      classes.push(styles.opened);
    }
    if (error) {
      classes.push(styles.error);
    }
    if (disabled) {
      classes.push(styles.disabled);
    }
    return classes.join(' ').trim();
  }, [opened, className, disabled, inline, error]);

  const handleToggle = () => {
    if (disabled) return;
    setOpened(!opened);
  };
  const handleChange = useCallback(option => () => {
    onChange && onChange(option);
  }, [onChange]);

  const selectedValue = useMemo(() => {
    if (!value) return null;
    return options.find(o => `${o.data}` === `${value.data}`);
  }, [options, value]);

  const handleChangeSearch = useCallback((value) => {
    setSearch(value);
  }, []);

  const handleStop = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const sorted = useMemo(() => {
    if (!options) return [];
    if (!!doNotSort) return options;
    return [...options].sort((a, b) => b.created.localeCompare(a.created));
  }, [options, doNotSort]);

  const filtered = useMemo(() => {
    if (!search) return sorted;
    const s = search.toLowerCase();
    return sorted.filter(o => o.name?.toLowerCase().includes(s));
  }, [sorted, search]);

  const convertDuration = (meta) => {
    if (meta.length > 0) {
      const metaObj = JSON.parse(meta.toString())

      if (metaObj?.duration !== undefined) {
        return new Date(metaObj?.duration * 1000).toISOString().substr(14, 5);
      }
    }
    return ''
  };

  return (
    <div className={wrapperClasses} ref={wrapperRef} onClick={handleToggle}>
      {(renderLabel || label) ? (
        <div className={styles.header}>
          {typeof renderLabel === 'function' ? renderLabel(<div className={styles.headerLabel}>{label}</div>) :
            (label ? <div className={styles.headerLabel}>{label}</div> : null)}
        </div>
      ) : null}
      <div className={styles.selectWrapper}>
        <div className={styles.select}>
          {loading ? <div style={{ marginLeft: 10 }}><Spinner width={20} height={20}/></div> : selectedValue ?
            <div className={styles.selectedTitle}>{selectedValue.name}</div> :
            <div className={styles.placeholder}>
              {placeholder}
            </div>}
          <div className={styles.chevronWrapper}>
            <ChevronIcon className={styles.chevron}/>
          </div>
        </div>
        {subTitle && <div className={styles.subtitle}>{subTitle}</div>}
        {opened &&
          <ul className={`${styles.list}${menuClassName ? ` ${menuClassName}` : ''}`}>
            {showSearch && <li className={styles.search} onClick={handleStop}>
              <TextInput allowSpecialCharacters autoFocus={searchAutoFocus} placeholder={'Search'} label={''} id={'search'} value={search}
                         onChange={handleChangeSearch} inDropDown={inDropDown}/>
            </li>}
            {filtered.length ?
              filtered.map(o => {
                return (
                  <li key={o.data} onClick={handleChange(o)} className={`${styles.listItem} ${selectedValue?.data === o.data ? styles.selectedItem : ''}`}>
                    <div style={{ display: "flex" }}>{o.name}</div>
                    <div style={{ display: "flex", width: 180, justifyContent: "space-between" }}>
                      <div>{o.created ? moment(Number(o.created)).format('MMM DD, YYYY') : ''}</div>
                      <div>{convertDuration(o.meta)}</div>
                    </div>
                  </li>
                );
              })
              : <li className={styles.listItem}>No options</li>
            }
          </ul>
        }
      </div>
    </div>
  );
}

export default VideoLibrarySelection;
